import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const useHojaPesadorData = () => {
  const [hojas, setHojas] = useState([]);
  const [trabajadores, setTrabajadores] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [inspectores, setInspectores] = useState([]);
  const [liquidaciones, setLiquidaciones] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 
        Authorization: `Bearer ${token}`,
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      };
      
      // Agregar timestamp para evitar caché del navegador
      const timestamp = new Date().getTime();

      const [hojasRes, trabajadoresRes, vehiculosRes, inspectoresRes, liquidacionesRes] = await Promise.all([
        axios.get(`${API}/hoja-pesador?_t=${timestamp}`, { headers }),
        axios.get(`${API}/trabajadores?_t=${timestamp}`, { headers }),
        axios.get(`${API}/vehiculos?_t=${timestamp}`, { headers }),
        axios.get(`${API}/inspectores?_t=${timestamp}`, { headers }),
        axios.get(`${API}/liquidaciones-mensuales?_t=${timestamp}`, { headers }),
      ]);

      setHojas(hojasRes.data);
      setTrabajadores(trabajadoresRes.data);
      setVehiculos(vehiculosRes.data);
      setInspectores(inspectoresRes.data);
      setLiquidaciones(liquidacionesRes.data || []);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const createHoja = async (formData) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.post(`${API}/hoja-pesador`, formData, { headers });
      setHojas(prev => [...prev, response.data]);
      toast.success('Hoja del pesador creada exitosamente');
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error al crear hoja:', error);
      toast.error(error.response?.data?.detail || 'Error al crear la hoja');
      return { success: false, error };
    }
  };

  const updateHoja = async (hojaId, formData) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.put(`${API}/hoja-pesador/${hojaId}`, formData, { headers });
      setHojas(prev => prev.map(h => h.id === hojaId ? response.data : h));
      toast.success('Hoja del pesador actualizada exitosamente');
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error al actualizar hoja:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar la hoja');
      return { success: false, error };
    }
  };

  const deleteHoja = async (hojaId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.delete(`${API}/hoja-pesador/${hojaId}`, { headers });
      setHojas(prev => prev.filter(h => h.id !== hojaId));
      toast.success('Hoja eliminada exitosamente');
      return { success: true };
    } catch (error) {
      console.error('Error al eliminar hoja:', error);
      toast.error('Error al eliminar la hoja');
      return { success: false, error };
    }
  };

  const createInspector = async (inspectorData) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.post(`${API}/inspectores`, inspectorData, { headers });
      setInspectores(prev => [...prev, response.data]);
      toast.success('Inspector creado exitosamente');
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error al crear inspector:', error);
      toast.error('Error al crear el inspector');
      return { success: false, error };
    }
  };

  const checkBoletaDuplicada = async (numeroBoleta) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(
        `${API}/hoja-pesador/validar-boleta/${numeroBoleta}`, 
        { headers }
      );
      return response.data.existe;
    } catch (error) {
      console.error('Error al validar boleta:', error);
      return false;
    }
  };

  const upsertLiquidacion = async (data) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/liquidaciones-mensuales`, data, { headers });
      setLiquidaciones(prev => {
        const idx = prev.findIndex(l => l.año === data.año && l.mes === data.mes);
        if (idx >= 0) {
          const copy = [...prev];
          copy[idx] = res.data;
          return copy;
        }
        return [...prev, res.data];
      });
      return { success: true, data: res.data };
    } catch (error) {
      console.error('Error al guardar liquidación:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar la liquidación');
      return { success: false, error };
    }
  };

  const deleteLiquidacion = async (año, mes) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/liquidaciones-mensuales/${año}/${mes}`, { headers });
      setLiquidaciones(prev => prev.filter(l => !(l.año === año && l.mes === mes)));
      return { success: true };
    } catch (error) {
      console.error('Error al eliminar liquidación:', error);
      return { success: false, error };
    }
  };

  return {
    hojas,
    trabajadores,
    vehiculos,
    inspectores,
    liquidaciones,
    loading,
    fetchData,
    createHoja,
    updateHoja,
    deleteHoja,
    createInspector,
    checkBoletaDuplicada,
    upsertLiquidacion,
    deleteLiquidacion,
  };
};
